import React, { createContext, useContext, useRef } from 'react'
import useTranslation from '../shared/TranslationProvider'

const CareerExplorationContext = createContext()

const useCareerExploration = () => {
  return useContext(CareerExplorationContext)
}

export default useCareerExploration

export const CareerExplorationProvider = ({
  careers,
  salary_hidden_for_locale,
  children,
}) => {
  const compare = careers.length === 2
  const { t } = useTranslation()
  const sections = t('sections')

  const careerDescriptionRef = useRef(null)
  const jobTasksRef = useRef(null)
  const responsibilitiesRef = useRef(null)
  const educationAndExperienceRef = useRef(null)
  const salaryRef = useRef(null)
  const traitsRef = useRef(null)
  const technologySkillsRef = useRef(null)
  const relatedOccupationsRef = useRef(null)
  const videoRef = useRef(null)

  const getSectionTitle = (isToc, t, name) => {
    const tocTitle = t('tocTitle', { name })
    const title = t('title', { name })
    if (!isToc || tocTitle.includes('javascript')) return title
    return tocTitle
  }

  const textifySkillInfo = (info) => {
    const infoArr = info.map((s) => s[0])
    return infoArr.join(', ')
  }

  const formatMoneyAmt = (amt) => {
    if (amt === null) return null
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amt)
    return formatted
  }

  const careerDescription = (career) => {
    return career?.description
  }

  const keyResponsibilities = (career) => {
    return career?.key_responsibilities
  }

  const requiredEducation = (career) => {
    return career?.required_education
  }

  const relatedWorkExperience = (career) => {
    return career?.related_work_experiences
  }

  const showRequiredEducation =
    !!requiredEducation(careers[0])?.length ||
    !!requiredEducation(careers[1])?.length

  const showRelatedWorkExperience =
    !!relatedWorkExperience(careers[0])?.length ||
    !!relatedWorkExperience(careers[1])?.length

  const showEducationAndExperience =
    showRequiredEducation || showRelatedWorkExperience

  const refMap = {
    0: careerDescriptionRef,
    1: videoRef,
    2: educationAndExperienceRef,
    3: responsibilitiesRef,
    4: salaryRef,
    5: relatedOccupationsRef,
    6: jobTasksRef,
    7: traitsRef,
    8: technologySkillsRef,
  }

  const hasTraits = (career) => {
    const personality_traits = career?.personality_traits
    if (!personality_traits?.length) return false
    return (
      personality_traits[0]?.length > 0 ||
      personality_traits[1]?.length > 0 ||
      personality_traits[2]?.length > 0
    )
  }

  const showSalaryData = () => {
    if (salary_hidden_for_locale) return false
    if (!careers[0].salary_data && !careers[1]?.salary_data) return false
    const {
      salary_data: { twentyfive, median, seventyfive },
    } = careers[0]
    if (twentyfive && median && seventyfive) return true
    if (careers[1]?.salary_data) {
      const {
        salary_data: {
          twentyfive: twentyfive2,
          median: median2,
          seventyfive: seventyfive2,
        },
      } = careers[1]
      return twentyfive2 && median2 && seventyfive2
    }
  }

  const value = {
    compare,
    careers,
    textifySkillInfo,
    sections,
    careerDescriptionRef,
    jobTasksRef,
    responsibilitiesRef,
    educationAndExperienceRef,
    salaryRef,
    traitsRef,
    technologySkillsRef,
    relatedOccupationsRef,
    refMap,
    videoRef,
    formatMoneyAmt,
    getSectionTitle,
    showEducationAndExperience,
    requiredEducation,
    relatedWorkExperience,
    careerDescription,
    keyResponsibilities,
    hasTraits,
    showSalaryData,
  }

  return (
    <CareerExplorationContext.Provider value={value}>
      {children}
    </CareerExplorationContext.Provider>
  )
}
